import { DUMMY_PIECE, MAX_HRLIMIT, TORSO_UP_ID } from '../data-provider/data-provider.module'
import ArmorPiece from '../data-provider/models/equipment/ArmorPiece'
import ArmorType from '../data-provider/models/equipment/ArmorType'
import Charm from '../data-provider/models/equipment/Charm'
import EquipmentCategory from '../data-provider/models/equipment/EquipmentCategory'
import EquipmentMin from '../data-provider/models/equipment/EquipmentMin'
import EquipmentSkills from '../data-provider/models/equipment/EquipmentSkills'
import HRlimit from '../data-provider/models/equipment/HRlimit'
import SkilledItem from '../data-provider/models/equipment/SkilledItem'
import Slots from '../data-provider/models/equipment/Slots'
import SkillActivation from '../data-provider/models/skills/SkillActivation'

const filterType = (piece: ArmorPiece, type: ArmorType) => {
  return piece.type === ArmorType.ALL || piece.type === type
}

const filterExclusions = (piece: ArmorPiece, exclusionNames: string[]) => {
  return !exclusionNames.includes(piece.name)
}

const filterHRlimit = (item: SkilledItem, hrlimit: HRlimit) => {
  return item.hrlimit <= hrlimit
}

const filterHasSkill = (item: SkilledItem, desiredSkills: SkillActivation[]) => {
  return desiredSkills.some((act) => {
    const s = item.skills.get(act.requiredSkill)
    return s !== undefined && s !== 0
  })
}

const applyHRlimitFilter = (items: SkilledItem[], hrlimit: HRlimit) => {
  if (hrlimit === MAX_HRLIMIT) return items
  return items.filter(x => filterHRlimit(x, hrlimit))
}

const applyCharmFilter = (charms: Charm[], skills: SkillActivation[]) => {
  // find generic slot charms
  const genericSlotCharms: Charm[] = []
  for (const slots of [3, 2, 1]) {
    const x = charms.find(c => c.slots === slots)
    if (x) {
      const newC: Charm = {
        name: `${slots}孔护符`,
        slots: slots as Slots,
        category: EquipmentCategory.CHARM,
        hrlimit: 0,
        skills: new EquipmentSkills(),
      }
      genericSlotCharms.push(newC)
    }
  }

  // build list of charms with wanted skills or with slots
  const result = charms
    .filter(x => filterHasSkill(x, skills))
    .concat(...genericSlotCharms)

  // return list with dummy charm if there are no pieces
  if (result.length === 0) {
    return [{
      ...DUMMY_PIECE,
      category: EquipmentCategory.CHARM,
    }]
  }

  return result
}

const applyArmorFilter = (
  pieces: ArmorPiece[],
  hrlimit: HRlimit,
  type: ArmorType,
  category: EquipmentCategory,
  pin: EquipmentMin | undefined,
  exclusions: EquipmentMin[],
  skills: SkillActivation[],
  defenseMode: 'min' | 'max',
) => {
  if (pin) return [pieces.find(x => x.name === pin.name)!];

  const excludedNames = exclusions.map(e => e.name);

  const hrlimitFiltered = applyHRlimitFilter(pieces, hrlimit) as ArmorPiece[];
  const typeFiltered = hrlimitFiltered.filter(p => filterType(p, type));
  const exclusionFiltered = typeFiltered.filter(p => filterExclusions(p, excludedNames));
  const sorted = exclusionFiltered.sort((a, b) => {
    const aDef = defenseMode === 'min' ? a.defense.base : a.defense.max;
    const bDef = defenseMode === 'min' ? b.defense.base : b.defense.max;
    return bDef - aDef;
  });

  const skillMatched = sorted.filter(x => filterHasSkill(x, skills));
  const matchedNames = new Set(skillMatched.map(x => x.name));

  const extraPieces: ArmorPiece[] = [];

  for (const slots of [3, 2, 1] as Slots[]) {
    const foundList = sorted.filter(
      p => p.slots === slots && !matchedNames.has(p.name) && filterExclusions(p, excludedNames)
    );
    extraPieces.push(...foundList);
    foundList.forEach(p => matchedNames.add(p.name));
  }

  const torsoUpList = sorted.filter(
  p => p.skills.has(TORSO_UP_ID) && !matchedNames.has(p.name) && filterExclusions(p, excludedNames)
  );
  extraPieces.push(...torsoUpList);
  torsoUpList.forEach(p => matchedNames.add(p.name));

  const result = skillMatched.concat(extraPieces);

  if (result.length === 0) {
    return [{
      ...DUMMY_PIECE,
      type,
      category,
    }];
  }

  return result;
};

export {
  filterType,
  filterHRlimit,
  filterHasSkill,
  applyHRlimitFilter,
  applyCharmFilter,
  applyArmorFilter,
}
